import { cn } from '@/lib/utils'

import { useBannerSliderVariants } from '../../../Theme'
import { SectionTitle } from '../../../UiBlocks'

import type { BannerItemProps } from '../../types'

/**
 * BannerItem - Presentational component for a single banner
 *
 * Features:
 * - Responsive images (desktop/mobile)
 * - Gradient fallback when no images
 * - Uses CVA variants via UiProvider
 * - LCP optimization: first banner loads eagerly with high priority
 *
 * @example
 * <BannerItem banner={{ id: 1, title: 'Sale', imageDesktop: '/img.jpg' }} isFirst />
 */
export function BannerItem({ banner, className, isFirst = false }: BannerItemProps) {
  const {
    bannerItemVariants,
    bannerLinkVariants,
    bannerImageVariants,
    bannerFallbackVariants,
    bannerFallbackContentVariants,
    bannerFallbackTitleVariants,
    bannerFallbackCtaVariants,
  } = useBannerSliderVariants()

  const altText = banner.title || 'Banner'
  const hasImages = banner.imageDesktop || banner.imageMobile

  // LCP optimization: first visible banner should load eagerly
  const loadingStrategy = isFirst ? 'eager' : 'lazy'
  const fetchPriority = isFirst ? 'high' : undefined

  const Wrapper = banner.link ? 'a' : 'div'
  const wrapperProps = banner.link
    ? { href: banner.link, 'aria-label': altText }
    : {}

  return (
    <div className={cn(bannerItemVariants(), className)}>
      <Wrapper
        className={bannerLinkVariants()}
        {...wrapperProps}
      >
        {hasImages ? (
          <>
            {/* Desktop Image */}
            {banner.imageDesktop && (
              <img
                src={banner.imageDesktop}
                alt={altText}
                width={1920}
                height={600}
                className={bannerImageVariants({ visibility: 'desktop' })}
                loading={loadingStrategy}
                fetchPriority={fetchPriority}
              />
            )}
            {/* Mobile Image */}
            {banner.imageMobile && (
              <img
                src={banner.imageMobile}
                alt={altText}
                width={750}
                height={600}
                className={bannerImageVariants({ visibility: 'mobile' })}
                loading={loadingStrategy}
                fetchPriority={fetchPriority}
              />
            )}
          </>
        ) : (
          /* Fallback gradient when no images */
          <div className={bannerFallbackVariants()}>
            <div className={bannerFallbackContentVariants()}>
              <SectionTitle as="h2" className={bannerFallbackTitleVariants()}>{banner.title}</SectionTitle>
              {banner.linkText && (
                <span className={bannerFallbackCtaVariants()}>
                  {banner.linkText}
                </span>
              )}
            </div>
          </div>
        )}
      </Wrapper>
    </div>
  )
}
